import React, { useEffect, useState } from "react";
import { Power, Clock, ShieldCheck, Users, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";

const inp = "bg-ivory border border-champagne rounded-md h-11 px-3 text-sm text-cocoa outline-none focus:border-mauve";
const PRESETS = [5, 10, 15, 30];

export function AdminMaintenance() {
  const [st, setSt] = useState(null);
  const [minutes, setMinutes] = useState(10);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [tick, setTick] = useState(0);

  const load = () => api.get("/admin/maintenance").then((r) => {
    setSt(r.data); setMinutes(r.data.countdown_minutes || 10);
    setMessage(r.data.message && r.data.message !== r.data.default_message ? r.data.message : "");
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { const id = setInterval(() => setTick((n) => n + 1), 1000); return () => clearInterval(id); }, []);

  const save = async (enabled) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/maintenance", { enabled, countdown_minutes: Number(minutes), message: message.trim() || null });
      setSt(r.data); setConfirm(false);
      window.dispatchEvent(new CustomEvent("ss:maintenance", { detail: r.data }));
      toast.success(enabled ? `Maintenance mode ON — members are signed out in ${minutes} min` : "Maintenance mode OFF — members can sign in again");
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };

  const on = !!st?.enabled;
  const remaining = on && st?.lockout_at ? Math.max(0, Math.floor((new Date(st.lockout_at).getTime() - Date.now()) / 1000)) : null;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  void tick;

  return (
    <div className="space-y-4" data-testid="admin-maintenance">
      <div className={`ss-card p-5 border ${on ? "border-rose/60" : "border-champagne"}`} data-testid="maintenance-status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Status</p>
            <p className="mt-1 font-heading text-2xl text-cocoa" data-testid="maintenance-status">
              {!st ? "Loading…" : on ? (remaining === 0 ? "ON — members locked out" : `ON — members signed out in ${fmt(remaining ?? 0)}`) : "OFF — site is open"}
            </p>
            {on && <p className="text-xs text-cocoa/55 mt-1">Enabled by {st.enabled_by} · lockout at {new Date(st.lockout_at).toLocaleTimeString()}</p>}
          </div>
          {on ? (
            <button onClick={() => save(false)} disabled={busy} data-testid="maintenance-off" className="ss-pill ss-pill-primary h-11 disabled:opacity-50"><Power size={14} /> Turn maintenance OFF</button>
          ) : confirm ? (
            <div className="flex gap-2 items-center">
              <button onClick={() => save(true)} disabled={busy} data-testid="maintenance-on-confirm" className="ss-pill h-11 bg-rose text-ivory disabled:opacity-50"><Power size={14} /> Yes, lock the site in {minutes} min</button>
              <button onClick={() => setConfirm(false)} className="ss-pill ss-pill-ghost h-11" data-testid="maintenance-on-cancel">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setConfirm(true)} disabled={!st} data-testid="maintenance-on" className="ss-pill ss-pill-primary h-11 disabled:opacity-50"><Power size={14} /> Turn maintenance ON</button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="ss-card p-5">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/50 inline-flex items-center gap-1.5"><Clock size={11} /> Countdown for signed-in members</p>
          <p className="text-xs text-cocoa/60 mt-1">Members currently using the site see a countdown banner and are signed out when it ends. Their unsaved text is kept in their browser and restored afterwards.</p>
          <div className="mt-3 flex gap-2 flex-wrap items-center">
            {PRESETS.map((m) => <button key={m} onClick={() => setMinutes(m)} data-testid={`maintenance-preset-${m}`} className={`ss-pill h-9 text-xs ${Number(minutes) === m ? "ss-pill-primary" : "ss-pill-ghost"}`}>{m} min</button>)}
            <input type="number" min={1} max={120} value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="maintenance-minutes" className={`${inp} w-24`} aria-label="Countdown minutes" />
          </div>
          {on && <button onClick={() => save(true)} disabled={busy} data-testid="maintenance-restart-countdown" className="mt-3 text-xs text-mauve underline">Apply new countdown (restarts the timer from now)</button>}
        </div>
        <div className="ss-card p-5">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/50 inline-flex items-center gap-1.5"><MessageSquare size={11} /> Message on the maintenance page</p>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={300} rows={3} data-testid="maintenance-message-input"
            placeholder="We'll be back online in a few minutes with a brand new, exciting update." className="mt-2 w-full bg-ivory border border-champagne rounded-md p-3 text-sm outline-none focus:border-mauve" />
          <p className="text-[11px] text-cocoa/50 mt-1">Leave blank for the default line above. {on && <button onClick={() => save(true)} className="text-mauve underline">Update message</button>}</p>
        </div>
      </div>

      <div className="ss-card p-5 text-sm text-cocoa/75 leading-relaxed">
        <p className="text-[10px] uppercase tracking-widest text-cocoa/50 inline-flex items-center gap-1.5"><ShieldCheck size={11} /> What happens when it's on</p>
        <ul className="mt-2 space-y-1.5 list-disc pl-5">
          <li><strong>Immediately:</strong> every page — including the homepage and pricing — shows the maintenance page to visitors. Only admin accounts can sign in (via the discreet "Team sign-in" link).</li>
          <li><strong>Members already signed in</strong> keep working through the countdown, see the timer banner, then are signed out and shown "your work is safe". Every submission they make before that is stored as normal.</li>
          <li><strong>You</strong> see a dark status strip at the top of every page and can use the whole site as usual. Deploy, check, then turn maintenance OFF here.</li>
        </ul>
        <p className="mt-3 text-xs text-cocoa/50 inline-flex items-center gap-1.5"><Users size={11} /> Tip: turn it on ~10 minutes before you deploy so nobody is mid-sentence when the new build lands.</p>
      </div>
    </div>
  );
}
